import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Space, message, Alert, DatePicker, Radio, Checkbox, Tooltip } from 'antd';
import { LockOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import QRCode from 'react-qr-code';
import { copyToClipboard } from '../../helpers/clipboard';
import { useTranslation } from '../../hooks/useTranslation';
import { useUserInfo } from '../../contexts/UserInfoContext';

interface EncryptedLinkModalProps {
  /** 是否显示弹框 */
  open: boolean;
  /** 关闭弹框的回调 */
  onCancel: () => void;
  /** 上传记录ID */
  uploadId: number;
  /** 文件名 */
  fileName: string;
}

/**
 * 加密链接弹框组件
 * 用于创建带密码保护或免密访问的图片/视频链接
 * 
 * @author dongdong.fdd
 * @date 2026/01/12
 */
const EncryptedLinkModal: React.FC<EncryptedLinkModalProps> = ({
  open,
  onCancel,
  uploadId,
  fileName,
}) => {
  const { t } = useTranslation();
  const { isActiveVip } = useUserInfo();
  const [password, setPassword] = useState<string>('');
  const [remark, setRemark] = useState<string>('');
  const [expireMode, setExpireMode] = useState<'never' | 'custom'>('never');
  const [expireAt, setExpireAt] = useState<Dayjs | null>(null);
  const [hideFooter, setHideFooter] = useState(false);
  const [encryptedUrl, setEncryptedUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false);

  // 重置状态
  useEffect(() => {
    if (open) {
      setPassword('');
      setRemark('');
      setExpireMode('never');
      setExpireAt(null);
      setHideFooter(false);
      setEncryptedUrl('');
      setCreated(false);
    }
  }, [open]);

  // 创建加密链接
  const handleCreate = async () => {
    if (password && password.length < 4) {
      message.error(t('encryptedLinkModal.passwordMinLength'));
      return;
    }

    if (expireMode === 'custom' && (!expireAt || !expireAt.isAfter(dayjs()))) {
      message.error(t('encryptedLinkModal.expireInvalid'));
      return;
    }

    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('uploadId', uploadId.toString());
      if (password) {
        formData.append('password', password);
      }
      if (remark) {
        formData.append('remark', remark);
      }
      if (expireMode === 'custom' && expireAt) {
        formData.append('expireTime', expireAt.toISOString());
      }
      if (hideFooter) {
        formData.append('hideFooter', 'true');
      }

      const response = await fetch('/e/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      const json = await response.json();

      if (json.success) {
        setEncryptedUrl(json.encryptedUrl);
        setCreated(true);
        message.success(t('encryptedLinkModal.createSuccess'));
      } else {
        message.error(json.message || t('encryptedLinkModal.createFailed'));
      }
    } catch (error) {
      console.error('创建加密链接失败:', error);
      message.error(t('encryptedLinkModal.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 复制链接
  const handleCopy = () => {
    if (encryptedUrl) {
      copyToClipboard(encryptedUrl, t('encryptedLinkModal.linkCopied'));
    }
  };

  // 继续创建新链接
  const handleCreateAnother = () => {
    setPassword('');
    setRemark('');
    setExpireMode('never');
    setExpireAt(null);
    setEncryptedUrl('');
    setCreated(false);
  };

  return (
    <Modal
      title={
        <Space>
          <LockOutlined />
          <span>{t('encryptedLinkModal.title')}</span>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={
        <Space>
          {created ? (
            <>
              <Button onClick={handleCreateAnother}>
                {t('encryptedLinkModal.createNew')}
              </Button>
              <Button type="primary" onClick={handleCopy}>
                {t('encryptedLinkModal.copyLink')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" onClick={handleCreate} loading={loading}>
                {t('encryptedLinkModal.create')}
              </Button>
            </>
          )}
        </Space>
      }
      width={500}
    >
      {!created ? (
        <div style={{ padding: '12px 0' }}>
          <Alert
            message={t('encryptedLinkModal.descriptionTitle')}
            description={
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>{t('encryptedLinkModal.description1')}</li>
                <li>{t('encryptedLinkModal.description2')}</li>
                <li>{t('encryptedLinkModal.description3')}</li>
                <li>{t('encryptedLinkModal.description4')}</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>{t('encryptedLinkModal.fileName')}</div>
            <Input value={fileName} disabled />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>
              {t('encryptedLinkModal.setPassword')}
            </div>
            <Input
              placeholder={t('encryptedLinkModal.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: '8px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>
              {t('encryptedLinkModal.remarkLabel')}
            </div>
            <Input
              placeholder={t('encryptedLinkModal.remarkPlaceholder')}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              maxLength={200}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>{t('encryptedLinkModal.expireLabel')}</div>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio.Group
                value={expireMode}
                onChange={(event) => setExpireMode(event.target.value)}
              >
                <Radio value="never">{t('encryptedLinkModal.neverExpire')}</Radio>
                <Radio value="custom">{t('encryptedLinkModal.customExpire')}</Radio>
              </Radio.Group>
              {expireMode === 'custom' && (
                <DatePicker
                  showTime
                  value={expireAt}
                  disabledDate={(date) => date.isBefore(dayjs().startOf('day'))}
                  onChange={setExpireAt}
                  style={{ width: '100%' }}
                  placeholder={t('encryptedLinkModal.expirePlaceholder')}
                />
              )}
            </Space>
          </div>

          <div>
            <Tooltip title={!isActiveVip ? t('encryptedLinkModal.hideFooterVipOnly') : ''}>
              <Checkbox
                checked={hideFooter}
                onChange={(event) => setHideFooter(event.target.checked)}
                disabled={!isActiveVip}
              >
                {t('encryptedLinkModal.hideFooterLabel')}
                {!isActiveVip && <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>(VIP)</span>}
              </Checkbox>
            </Tooltip>
            <div style={{ marginTop: '4px', color: '#999', fontSize: '12px' }}>
              {t('encryptedLinkModal.hideFooterHint')}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '12px 0' }}>
          <Alert
            message={t('encryptedLinkModal.createSuccessMessage')}
            type="success"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 500 }}>{t('encryptedLinkModal.encryptedLink')}</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: '#f5f5f5',
                borderRadius: '4px',
                wordBreak: 'break-all',
              }}
            >
              <a href={encryptedUrl} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
                {encryptedUrl}
              </a>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={handleCopy}
                title={t('encryptedLinkModal.copyLink')}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <QRCode
              size={128}
              style={{ height: 'auto', maxWidth: '40%', width: '40%' }}
              value={encryptedUrl}
              viewBox="0 0 128 128"
            />
          </div>

          <div style={{ textAlign: 'center', marginTop: '12px', color: '#666', fontSize: '13px' }}>
            {t('encryptedLinkModal.shareHint')}
          </div>

          {remark && (
            <div style={{ marginTop: '16px', color: '#666', fontSize: '13px' }}>
              {t('encryptedLinkModal.remark')}: {remark}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default EncryptedLinkModal;
